import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, KeyRound, IdCard } from "lucide-react";
import { http, formatApiError, askDelete } from "../../lib/api";

const inp = "border border-slate-300 rounded-md px-3 py-2 text-sm bg-white";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function SigLink({ fid, label }) {
  const open = async () => {
    const w = window.open("", "_blank");
    try {
      const r = await http.get(`/files/${fid}/download`, { responseType: "blob" });
      w.location.href = URL.createObjectURL(r.data);
    } catch { w?.close(); toast.error("Signature indisponible"); }
  };
  return <button type="button" data-testid={`sig-${fid}`} onClick={open} className="text-sky-700 hover:underline">{label}</button>;
}

export function KeysAdmin({ siteId }) {
  const [keys, setKeys] = useState([]);
  const [loans, setLoans] = useState([]);
  const [f, setF] = useState({ label: "", kind: "cle", notes: "" });
  const load = () => Promise.all([http.get("/site-keys", { params: { site_id: siteId } }), http.get("/site-keys/loans", { params: { site_id: siteId } })])
    .then(([k, l]) => { setKeys(k.data); setLoans(l.data); });
  useEffect(() => { if (siteId) load(); }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async (e) => {
    e.preventDefault();
    try { await http.post("/site-keys", { site_id: siteId, ...f }); setF({ ...f, label: "", notes: "" }); load(); } catch (er) { toast.error(formatApiError(er)); }
  };
  const del = async (k) => {
    if (!askDelete(`Supprimer « ${k.label} » ?`)) return;
    try { await http.delete(`/site-keys/${k.id}`); load(); } catch (er) { toast.error(formatApiError(er)); }
  };
  return (
    <div className="space-y-4" data-testid="keys-admin">
      <form onSubmit={add} className="flex flex-wrap gap-2 items-end bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="key-form">
        <select data-testid="key-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={inp}><option value="cle">Clé</option><option value="badge">Badge</option></select>
        <input data-testid="key-label" required value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} className={`${inp} flex-1 min-w-[200px]`} placeholder="Nom (ex. Clé local technique, Badge visiteur 3)" />
        <input data-testid="key-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={`${inp} flex-1 min-w-[160px]`} placeholder="Remarque (optionnel)" />
        <button data-testid="key-add" className="bg-[#0B2A7A] text-white text-sm font-semibold rounded-md px-4 py-2">Ajouter</button>
      </form>
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="keys-list">
        {keys.length === 0 && <div className="p-5 text-sm text-slate-500 text-center">Aucun badge ni clé sur ce site.</div>}
        {keys.map((k) => {
          const Icon = k.kind === "badge" ? IdCard : KeyRound;
          return (
            <div key={k.id} className="px-4 py-3 flex items-center gap-3" data-testid={`key-row-${k.id}`}>
              <Icon className="h-4 w-4 text-slate-500" />
              <div className="flex-1 min-w-0"><div className="font-semibold text-sm">{k.label}</div>{k.notes && <div className="text-xs text-slate-500">{k.notes}</div>}</div>
              <span className={`text-xs font-bold rounded px-2 py-0.5 ${k.loan ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800"}`} data-testid={`key-status-${k.id}`}>
                {k.loan ? `Prêté à ${k.loan.borrower_name}${k.loan.borrower_company ? ` (${k.loan.borrower_company})` : ""} depuis ${fmt(k.loan.lent_at)}` : "Disponible"}
              </span>
              <button data-testid={`key-delete-${k.id}`} onClick={() => del(k)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="loans-list">
        <div className="px-4 py-3 font-bold border-b border-slate-100">Historique des prêts</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr><th className="text-left px-4 py-2">Badge / clé</th><th className="text-left px-4 py-2">Emprunteur</th><th className="text-left px-4 py-2">Prêt</th><th className="text-left px-4 py-2">Retour</th><th className="text-left px-4 py-2">Signatures</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loans.length === 0 && <tr><td colSpan={5} className="p-5 text-center text-slate-500">Aucun prêt.</td></tr>}
            {loans.map((l) => (
              <tr key={l.id} data-testid={`loan-row-${l.id}`}>
                <td className="px-4 py-2 font-semibold">{l.label}</td>
                <td className="px-4 py-2">{l.borrower_name}{l.borrower_company && <div className="text-xs text-slate-500">{l.borrower_company}</div>}</td>
                <td className="px-4 py-2">{fmt(l.lent_at)}<div className="text-xs text-slate-500">{l.lent_by_name}</div></td>
                <td className="px-4 py-2">{l.returned_at ? <>{fmt(l.returned_at)}<div className="text-xs text-slate-500">{l.returned_to_name}</div></> : <span className="text-amber-700 font-semibold">En cours</span>}</td>
                <td className="px-4 py-2 text-xs space-x-2">{l.sig_out && <SigLink fid={l.sig_out} label="au prêt" />}{l.sig_in && <SigLink fid={l.sig_in} label="au retour" />}{!l.sig_out && !l.sig_in && "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
